/*
 * bfcm-gate.js — BFCM 2026 gate: pre-sale email price lock, preview-week
 * phone unlock, early-access buyability.
 *
 * Loaded (deferred) in the presale / preview / early_access stages only, from
 * snippets/bfcm-gate.liquid (markup, critical CSS, pre-paint unlock check and
 * window.__bfcmReveal live there).
 *
 * Price lock (html.bfcm-gate, presale + preview) — unchanged from step 2:
 *   - prices (and the Sale badge) of locked products are hidden behind a
 *     "BFCM price" chip until html.bfcm-unlocked;
 *   - Add to cart / Buy now / Shop Pay / cross-sell Add / wishlist "Move to bag"
 *     on a locked product opens the email step; after unlock the original click
 *     is replayed, so the add continues through the theme's own flow;
 *   - tapping a card in the hub's price_lock grid opens the sheet (once per session), unless
 *     the pre-sale page's waitlist panel is there: then cards navigate, and a locked price or
 *     Add to bag goes to the panel while it is on screen (the pop-up further down).
 * Preview week (html.bfcm-preview): drop products ([data-bfcm-drop="preview"],
 * snippets/bfcm-drop.liquid) are blurred. Tapping one, or "Unlock the preview",
 * opens the two-step sheet: email (step 1, unlocks pricing) → phone (step 2,
 * unlocks the preview). Email-unlocked devices only see step 2. SMS for US/CA,
 * WhatsApp elsewhere, with a switch. On unlock the real photos sharpen in,
 * names + launch prices appear and "Save for launch" (Swym wishlist) shows.
 * On a drop product page the page reloads so the real gallery initialises.
 * Early access (html.bfcm-ea): until 1:00 PM EST, devices that never unlocked
 * see "Opens 1:00 PM EST" instead of Add to bag on drop products.
 *
 * Klaviyo (public site key only; blank list IDs = QA mode, nothing is sent). The shopper
 * is unlocked first; requests go out in the background through a localStorage retry
 * queue (rastah_bfcm_q), never on the critical path:
 *   email → client/subscriptions (email marketing consent, email list) + client/profiles
 *           properties + client/events "Joined BFCM Waitlist"
 *   SMS   → client/subscriptions (SMS marketing consent, SMS list) + client/profiles properties
 *           + client/events "Added Phone to BFCM Waitlist"
 *   WhatsApp → client/profiles properties (whatsapp_opt_in, whatsapp_number (E.164),
 *           whatsapp_opt_in_at, whatsapp_opt_in_source) + client/events "Added Phone to BFCM
 *           Waitlist"; the team sends WhatsApp in-house.
 *   Event properties: surface, origin, channel (sms | whatsapp), phone_country, stage, page_path,
 *   utm_* (first touch), landing_path. Emails and numbers never go into the dataLayer.
 *   The team's Klaviyo pop-ups (settings.bfcm_klaviyo_popup_form_ids) subscribe on Klaviyo's side;
 *   their klaviyoForms submit / stepSubmit events unlock this device (see onKlaviyoForm).
 *
 * API: window.RastahBFCMGate = { open(origin), openPreview(origin), step2(origin), unlocked,
 *      phoneGiven, previewUnlocked, require(cb, origin), isLocked(el), canBuy(el), eaClosed(),
 *      success(kind), reset() }
 * Events on document: bfcm:unlock (email), bfcm:unlock-phone ({ channel }).
 *
 * Contract with the surfaces (menu, header badge, announcement, banner, footer, home hero, PDP line):
 *   (1) [data-bfcm-open="<origin>"]: a tap opens the gate with that origin (delegated, capture
 *       phase): locked → the sheet's email step; joined → a link is followed, a button opens step 2
 *       (no number yet) or the success screen. Nothing is intercepted outside pre-sale / preview week.
 *   (2) <div data-bfcm-surface="<name>" [data-bfcm-after="hub"] [data-bfcm-ease]> holding the
 *       bfcm-gate-form parts in [data-bfcm-step="email|phone|done|joined"] panels, exactly like the
 *       pre-sale page panel: email → step 2 → done; joined (+ re-ask) for returning devices.
 *       data-bfcm-after="hub": after the email step, go to the pre-sale page with step 2 waiting
 *       (?bfcm_step2=<origin>). data-bfcm-busy is set on the surface meanwhile.
 *   (3) html.bfcm-unlocked (joined / prices unlocked) and html.bfcm-phone (a number was given on
 *       this device; persisted in rastah_bfcm_preview) drive the surfaces' own CSS.
 * dataLayer: bfcm_gate_open, bfcm_gate_dismiss, bfcm_unlock_email,
 *            bfcm_unlock_phone, bfcm_save_for_launch, bfcm_typo_suggest, bfcm_typo_accept,
 *            bfcm_phone_skip, bfcm_auto_unlock (gate_trigger customer | klaviyo: known subscribers,
 *            setting bfcm_auto_unlock_known). bfcm_unlock_email / _phone carry gate_surface (where the form is:
 *            hub | pdp | home | banner | footer | menu | sheet), gate_origin (what opened it:
 *            card, price_chip, add_to_cart, quick_view, search, cart_cross_sell, wishlist_move,
 *            shop_the_look, lookbook_stories, menu, announcement, pdp_line, reask, … or the
 *            surface's own data-bfcm-origin) and the first-touch utm_source / utm_medium /
 *            utm_campaign / utm_content / utm_term.
 * Attribution: the first page view in these stages stores the first touch (rastah_bfcm_ft:
 * UTMs, fbclid / gclid presence, landing path); the email sign-up sends it to Klaviyo as
 * profile properties (bfcm_utm_*, bfcm_fbclid, bfcm_gclid, bfcm_landing_path,
 * bfcm_gate_surface, bfcm_gate_origin, bfcm_joined_at) through client/profiles; the phone
 * step adds bfcm_phone_surface and bfcm_phone_origin.
 * Existing subscribers (links from Klaviyo, read pre-paint by snippets/bfcm-gate.liquid and
 * stripped from the address bar): ?bfcm_unlock=1 → prices unlocked on this device;
 * ?bfcm_unlock=1&bfcm_preview=1 → also the number flag (preview open, html.bfcm-phone, no
 * re-ask), without asking for a number; ?bfcm_preview=1 alone does nothing. On a Preview
 * collection's page and its products, an identified browser (klaviyo.isIdentified()) sends
 * klaviyo.track('Viewed BFCM Drop', { collection, stage }) once per session. The onsite
 * klaviyo.track calls, like the API requests, are never sent in QA mode.
 * Success screen (step "done", snippets/bfcm-remind.liquid): after an email
 * unlock with nothing to resume, and after the phone step, the sheet shows
 * "You're on the list / You're in" + Add early access to calendar
 * (assets/bfcm-remind.js). Add to bag / require() unlocks keep the fast path
 * (close, toast, replay). After the phone step the reveal (or PDP reload) runs
 * when the success screen closes.
 */
(function () {
  'use strict';
  var root = document.documentElement;
  var GATE = root.classList.contains('bfcm-gate');
  var PREVIEW = root.classList.contains('bfcm-preview');
  var EA = root.classList.contains('bfcm-ea');
  if (!(GATE || EA) || window.RastahBFCMGate) return;

  var KEY = 'rastah_bfcm_unlock';
  var PKEY = 'rastah_bfcm_preview';
  var DISMISS_KEY = 'rastah_bfcm_gate_dismissed';
  var TOAST_KEY = 'rastah_bfcm_toast';
  var SAVE_TOAST_KEY = 'rastah_bfcm_save_toast'; // Save for launch toast already shown on this device
  var EM_KEY = 'rastah_bfcm_em';                 // sessionStorage: this session's email, for step 2 on a later page
  var KNOWN_KEY = 'rastah_bfcm_known';           // prices unlocked for a Klaviyo-identified browser (setting on)
  var REVISION = '2026-07-15';
  var cfg = {};
  try { cfg = JSON.parse(document.getElementById('bfcm-gate-config').textContent); } catch (e) {}

  var sheet = document.querySelector('[data-bfcm-sheet]');
  var toastEl = document.querySelector('[data-bfcm-toast]');
  var pending = null;      // swallowed click target (replayed after unlock) or a require() callback
  var lastFocus = null;
  var flow = 'sale';       // 'sale' (price lock) | 'preview' (two steps)
  var origin0 = 'unknown'; // what opened the current preview flow
  var sheetOrigin = 'sheet'; // what opened the sheet in the sale flow (attribution, see formOrigin)
  var justEmail = false;
  // Ties the phone step to the same Klaviyo profile. Kept for the browser session only
  // (sessionStorage), so step 2 on the next page (home → pre-sale page) still knows it.
  var lastEmail = '';
  try { lastEmail = sessionStorage.getItem(EM_KEY) || ''; } catch (e) {}
  var afterDone = null;    // runs when the success screen closes
  var resumeNext = null;   // sale flow in the sheet: the Add to bag that waits for step 2 to end

  var ATC = '[data-add-to-cart], button[name="add"], .quick-view__buy-now, .shopify-payment-button, ' +
            '.cross-sells__item-add[data-add-item-id], [data-mobile-sticky-atc-btn], [data-size-fit-cta]';
  var PRICE = '.product-item__price, .product__price, .product-card-mini__price, ' +
              '.quick-search__result-price, .cross-sells__item-price-wrapper';
  var MOVE = '[data-sheet-card-move], [data-quick-account-add-to-cart], [data-wishlist-card-move]';
  var HUB_GRID = '.shopify-section--bfcm-hub ~ .shopify-section';

  function unlocked() { return root.classList.contains('bfcm-unlocked'); }
  function previewOpen() { return root.classList.contains('bfcm-preview-open'); }
  function eaClosed() { return root.classList.contains('bfcm-ea-closed'); }
  function isSms() { return root.classList.contains('bfcm-ch-sms'); }
  // A phone / WhatsApp number was given on this device (rastah_bfcm_preview; html.bfcm-phone).
  function phoneGiven() { return root.classList.contains('bfcm-phone'); }
  // The early-access date only once it is confirmed (theme setting); otherwise no date at all.
  function launchAt() { return cfg.launchLabel ? String(cfg.launchLabel).replace(', ', ' at ') : ''; }

  // The hub nav (sections/bfcm-hub-nav.liquid) carries the active tab's mode.
  function hubMode() {
    var hub = document.querySelector('[data-bfcm-hub]');
    return hub ? hub.getAttribute('data-bfcm-mode') : null;
  }
  function hubLocked() { return GATE && hubMode() === 'price_lock'; }

  function lockedScope(el) {
    if (!GATE || !el || unlocked()) return false;
    if (el.closest('[data-bfcm-lock]')) return true;
    if (hubLocked() && el.closest(HUB_GRID + ' .product-item')) return true;
    // Teleported PDP UI (sticky bar, size & fit sheet) on a locked product page.
    if (root.classList.contains('bfcm-lock-pdp') && el.closest('[data-mobile-sticky-atc], [data-size-fit-modal]')) return true;
    return false;
  }

  // A blurred drop piece (preview week, before the phone step).
  function previewScope(el) {
    if (!PREVIEW || !el || previewOpen()) return false;
    if (el.closest('.product-item[data-bfcm-drop="preview"], .product-card-mini[data-bfcm-drop="preview"]')) return true;
    if (el.closest('.product[data-bfcm-drop="preview"] .product__primary-left')) return true;
    // Safety net: Preview tab collection not listed in "Preview collections".
    if (hubMode() === 'preview' && el.closest(HUB_GRID + ' .product-item')) return true;
    return false;
  }

  // A drop product's Add to bag during the early-access hour, on a device that isn't in.
  function eaScope(el) {
    if (!EA || !el || !eaClosed()) return false;
    if (el.closest('[data-bfcm-drop]')) return true;
    if (root.classList.contains('bfcm-drop-pdp') && el.closest('[data-mobile-sticky-atc], [data-size-fit-modal]')) return true;
    return false;
  }

  function dismissed() { try { return sessionStorage.getItem(DISMISS_KEY) === '1'; } catch (e) { return false; } }

  // ---------------------------------------------------------------- analytics
  function track(event, props) {
    var payload = Object.assign({ event: event, ts: Date.now(), page_path: location.pathname + location.search, bfcm_stage: cfg.stage || '' }, props || {});
    (window.dataLayer = window.dataLayer || []).push(payload);
    try {
      if (window.Shopify && Shopify.analytics && typeof Shopify.analytics.publish === 'function') Shopify.analytics.publish(event, payload);
    } catch (e) {}
  }
  function hubTab() { return (window.RastahBFCM || {}).tab || null; }

  // First-touch attribution (written pre-paint by snippets/bfcm-gate.liquid: rastah_bfcm_ft).
  var UTM = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
  function firstTouch() {
    try { return JSON.parse(localStorage.getItem('rastah_bfcm_ft') || '{}') || {}; } catch (e) { return {}; }
  }
  // dataLayer fields: utm_* ('' when absent).
  function utmFields() {
    var ft = firstTouch(), o = {};
    UTM.forEach(function (k) { o[k] = ft[k] || ''; });
    return o;
  }
  // Klaviyo profile properties: bfcm_utm_*, bfcm_fbclid / bfcm_gclid (true when the landing URL
  // had one), bfcm_landing_path, bfcm_gate_surface (where they joined).
  function attribProps(surface) {
    var ft = firstTouch(), o = {};
    UTM.forEach(function (k) { if (ft[k]) o['bfcm_' + k] = ft[k]; });
    o.bfcm_fbclid = !!ft.fbclid;
    o.bfcm_gclid = !!ft.gclid;
    if (ft.landing) o.bfcm_landing_path = ft.landing;
    if (surface) o.bfcm_gate_surface = surface;
    return o;
  }
  // Which surface a form belongs to: hub (pre-sale page panel), pdp (product-page box), sheet (pop-up).
  function surfaceName(form) {
    var sf = form && form.closest('[data-bfcm-surface]');
    if (sf) return sf.getAttribute('data-bfcm-surface');
    return form && form.closest('[data-bfcm-sheet]') ? 'sheet' : 'other';
  }
  /*
   * What opened the form (gate_origin in bfcm_unlock_email / _phone, Klaviyo bfcm_gate_origin and
   * the events' origin): the sheet → whatever opened it (card, price_chip, add_to_cart,
   * quick_view, search, cart_cross_sell, wishlist_move, shop_the_look, lookbook_stories, menu,
   * announcement, pdp_line, …); a surface → what sent the shopper there (price_chip,
   * add_to_cart, reask, the home form's redirect), else the form's own data-bfcm-origin (hub,
   * pdp, home, …).
   */
  function surfOrigin(sf) {
    if (sf.__bfcmOrigin) return sf.__bfcmOrigin;
    var f = sf.querySelector('[data-bfcm-form]');
    return (f && f.getAttribute('data-bfcm-origin')) || sf.getAttribute('data-bfcm-surface') || 'unknown';
  }
  function formOrigin(form) {
    if (!form) return 'unknown';
    if (form.closest('[data-bfcm-sheet]')) return flow === 'preview' ? origin0 : sheetOrigin;
    var sf = form.closest('[data-bfcm-surface]');
    if (sf) return surfOrigin(sf);
    return form.getAttribute('data-bfcm-origin') || 'unknown';
  }
  // A locked price / Add to bag: where it sits, when that says more than the control.
  function where(el, base) {
    if (!el || !el.closest) return base;
    if (el.closest('.quick-product--wrap')) return 'quick_view';
    if (el.closest('[data-quick-search], .quick-search')) return 'search';
    if (el.closest('[data-cross-sells]')) return 'cart_cross_sell';
    return base;
  }

  // ---------------------------------------------------------------- sheet
  function q(sel) { return sheet ? sheet.querySelector(sel) : null; }
  function qa(sel) { return sheet ? sheet.querySelectorAll(sel) : []; }

  function setFlow(f) {
    flow = f;
    qa('[data-bfcm-flow]').forEach(function (el) { el.hidden = el.getAttribute('data-bfcm-flow') !== f; });
    // The sale flow is the black waitlist pop-up; the preview flow keeps its white sheet.
    var panel = q('.bfcm-sheet__panel');
    if (panel) panel.classList.toggle('bfcm-dark', f === 'sale');
  }

  function showStep(step, opts) {
    opts = opts || {};
    var panel = null;
    qa('[data-bfcm-step]').forEach(function (el) {
      var on = el.getAttribute('data-bfcm-step') === step;
      el.hidden = !on;
      el.classList.remove('bfcm-sheet__step--in');
      if (on) panel = el;
    });
    if (!panel) return;
    // The sale flow's email step is the black pop-up; step 2 (the drawer) and the success screen are white.
    var sp = q('.bfcm-sheet__panel');
    if (sp) sp.classList.toggle('bfcm-dark', flow === 'sale' && step === 'email');
    if (opts.animate) { void panel.offsetWidth; panel.classList.add('bfcm-sheet__step--in'); }
    var dialog = q('[role="dialog"]');
    if (dialog) dialog.setAttribute('aria-labelledby', step === 'phone' ? (flow === 'sale' ? 'bfcm-sheet-step2-h' : 'bfcm-sheet-title-phone') : step === 'done' ? 'bfcm-sheet-title-done' : 'bfcm-sheet-title');
    if (step === 'phone' && flow === 'preview') {
      var two = !!opts.two, ok = q('[data-bfcm-okline]'), k = q('[data-bfcm-phone-k]'), dots = q('[data-bfcm-phone-dots]');
      if (ok) ok.hidden = !opts.justEmail;
      if (k) k.textContent = two ? 'Step 2 of 2' : 'Early look';
      if (dots) dots.hidden = !two;
    }
    if (opts.focus !== false) focusIn(panel);
  }

  /*
   * iPhone only raises the keyboard when focus() runs inside the tap that caused it, so the
   * field is focused synchronously, right after its panel is shown (never on a timer). The
   * step's entrance animation is opacity / transform only, which doesn't block focus.
   */
  function focusIn(panel) {
    var el = panel && (panel.querySelector('input:not([type="hidden"])') || panel.querySelector('[data-bfcm-done-h]'));
    if (!el) return;
    try { el.focus({ preventScroll: true }); } catch (e) { try { el.focus(); } catch (x) {} }
  }

  function openSheet(origin, f) {
    if (!sheet) return;
    sheetOrigin = origin || 'unknown';
    lastFocus = document.activeElement;
    // Park the sheet on <body> so no transformed ancestor traps position:fixed.
    if (sheet.parentNode !== document.body) document.body.appendChild(sheet);
    setFlow(f || 'sale');
    // A locked Add to bag is waiting: the Add to bag copy ("Unlock & add to bag").
    sheet.classList.toggle('is-atc', !!pending);
    // Visible first, then the step (and its focus) in the same tap.
    sheet.hidden = false;
    root.classList.add('bfcm-sheet-open');
    matchSheetHeight();
    showStep('email');
    track('bfcm_gate_open', { gate_origin: origin || 'unknown', gate_flow: flow, gate_step: 'email', gate_surface: 'sheet' });
  }

  /*
   * Phones, sale flow: the email pop-up is exactly as tall as the step 2 drawer, so the switch from
   * email to number doesn't jump. Measured with the phone step shown for one synchronous layout
   * (no paint in between), capped at 90% of the screen. Desktop keeps the centred box as it is.
   */
  var NAV_ORIGINS = { menu: 1, header_badge: 1, announcement: 1 };
  function matchSheetHeight() {
    var panel = q('.bfcm-sheet__panel');
    if (!panel) return;
    panel.style.minHeight = '';
    var ph = q('[data-bfcm-step="phone"]');
    if (!ph || flow !== 'sale' || !window.matchMedia || !window.matchMedia('(max-width: 749px)').matches) return;
    var steps = qa('[data-bfcm-step]'), was = [];
    steps.forEach(function (el) { was.push(el.hidden); el.hidden = el !== ph; });
    var h = panel.getBoundingClientRect().height;
    steps.forEach(function (el, i) { el.hidden = was[i]; });
    var cap = (window.innerHeight || root.clientHeight) * 0.9;
    if (h > 0) panel.style.minHeight = Math.round(Math.min(h, cap)) + 'px';
  }

  function openPreview(origin) {
    if (!sheet || !PREVIEW || previewOpen()) return;
    if (!q('[data-bfcm-step="phone"]')) return;
    lastFocus = document.activeElement;
    if (sheet.parentNode !== document.body) document.body.appendChild(sheet);
    pending = null;
    origin0 = origin || 'unknown';
    justEmail = false;
    setFlow('preview');
    var step = unlocked() ? 'phone' : 'email';
    sheet.hidden = false;
    root.classList.add('bfcm-sheet-open');
    matchSheetHeight();
    showStep(step, { two: false });
    track('bfcm_gate_open', { gate_origin: origin0, gate_flow: 'preview', gate_step: step, gate_surface: 'sheet' });
  }

  /*
   * The sheet straight on step 2 (sale flow), for a device whose email is already in: a re-ask
   * outside a surface, a [data-bfcm-open] tap after joining, the arrival on the pre-sale page
   * when its panel isn't showing. false when the sheet has no step 2.
   */
  function openStep2(origin) {
    if (!sheet || !GATE || !q('[data-bfcm-step="phone"]')) return false;
    sheetOrigin = origin || 'unknown';
    lastFocus = document.activeElement;
    if (sheet.parentNode !== document.body) document.body.appendChild(sheet);
    pending = null;
    resumeNext = null;
    setFlow('sale');
    sheet.classList.remove('is-atc');
    sheet.hidden = false;
    root.classList.add('bfcm-sheet-open');
    matchSheetHeight();
    showStep('phone', { animate: true });
    track('bfcm_gate_open', { gate_origin: origin || 'unknown', gate_flow: 'sale', gate_step: 'phone', gate_surface: 'sheet' });
    return true;
  }

  // What the success copy says on a later visit of a device that gave a number (no "reply Y");
  // a number given on this page keeps its own variant.
  function phoneDoneVariant() {
    var v = root.getAttribute('data-bfcm-done-v');
    if (!v || v === 'email') setDoneVariant(isSms() ? 'sms' : 'wa');
  }

  /*
   * Contract (1): an element with data-bfcm-open="<origin>" (menu, header_badge, announcement,
   * pdp_line, banner_cta, footer_cta, home_cta, …) opens the gate with that origin.
   *   prices locked             → the sheet's email step (then step 2, like every join);
   *   joined, it is a link      → the link is followed (e.g. "Shop pre-sale");
   *   joined, no number yet     → the sheet's step 2;
   *   joined with a number      → the sheet's success screen.
   * Outside pre-sale / preview week (no sheet, or this script isn't loaded) nothing is
   * intercepted, so a link still goes where it points. Returns true when it took the click.
   */
  function openFrom(el) {
    if (!sheet || !GATE) return false;
    var o = (el.getAttribute('data-bfcm-open') || '').trim() || 'button';
    if (!unlocked()) {
      pending = null;
      openSheet(o, 'sale');
      return true;
    }
    var a = el.closest('a[href]'), href = a ? (a.getAttribute('href') || '').trim() : '';
    if (href && href !== '#' && !/^javascript:/i.test(href)) return false;
    // On the list without a number, from the navigation: to the pre-sale page, where the drawer waits.
    if (!phoneGiven() && NAV_ORIGINS[o]) {
      var to = hubUrl(o);
      if (to) { location.assign(to); return true; }
    }
    if (!phoneGiven() && openStep2(o)) return true;
    phoneDoneVariant();
    if (showDone('sale')) {
      track('bfcm_gate_open', { gate_origin: o, gate_flow: 'sale', gate_step: 'done', gate_surface: 'sheet' });
      return true;
    }
    return false;
  }

  function closeSheet(byUser) {
    if (!sheet || sheet.hidden) return;
    var step = (q('[data-bfcm-step]:not([hidden])') || { getAttribute: function () { return 'email'; } }).getAttribute('data-bfcm-step');
    sheet.hidden = true;
    root.classList.remove('bfcm-sheet-open');
    if (step === 'done') {
      // Closing the success screen (✕, Esc, backdrop or Continue) is not a dismissal.
      if (lastFocus && lastFocus.focus) { try { lastFocus.focus({ preventScroll: true }); } catch (e) {} }
      var next = afterDone;
      afterDone = null;
      if (next) next();
      return;
    }
    if (step === 'phone' && flow === 'sale') {
      // The email is in: closing step 2 skips it (not a dismissal), and a waiting Add to bag goes through.
      if (byUser) skipped('sheet', 'close');
      if (lastFocus && lastFocus.focus) { try { lastFocus.focus({ preventScroll: true }); } catch (e) {} }
      var nx = resumeNext;
      resumeNext = null;
      resume(nx);
      return;
    }
    if (byUser) {
      pending = null;
      try { sessionStorage.setItem(DISMISS_KEY, '1'); } catch (e) {}
      track('bfcm_gate_dismiss', { gate_flow: flow, gate_step: step });
    }
    if (lastFocus && lastFocus.focus) { try { lastFocus.focus({ preventScroll: true }); } catch (e) {} }
  }

  function toast(text, ms) {
    if (!toastEl) return;
    if (toastEl.parentNode !== document.body) document.body.appendChild(toastEl);
    toastEl.querySelector('[data-bfcm-toast-text]').textContent = text;
    toastEl.classList.add('is-shown');
    clearTimeout(toast._t);
    toast._t = setTimeout(function () { toastEl.classList.remove('is-shown'); }, ms || 4200);
  }

  // Save for launch: the explaining toast shows on this device's first save only
  // (localStorage; if storage throws, at most once per page view). Every save and
  // unsave still updates the heart (aria-pressed) and this polite live region.
  var saveToastSeen = false;
  function firstSave() {
    if (saveToastSeen) return false;
    saveToastSeen = true;
    try {
      if (localStorage.getItem(SAVE_TOAST_KEY)) return false;
      localStorage.setItem(SAVE_TOAST_KEY, String(Date.now()));
    } catch (e) {}
    return true;
  }
  var liveEl = null;
  function announce(text) {
    if (!liveEl) return;
    liveEl.textContent = '';
    setTimeout(function () { liveEl.textContent = text; }, 60);
  }
  if (PREVIEW && document.body) {
    liveEl = document.createElement('span');
    liveEl.className = 'visually-hidden';
    liveEl.setAttribute('role', 'status');
    liveEl.setAttribute('aria-live', 'polite');
    liveEl.setAttribute('data-bfcm-save-live', '');
    document.body.appendChild(liveEl);
  }

  // ---------------------------------------------------------------- flags
  function setCookie(name, days) {
    try {
      document.cookie = name + '=1; path=/; max-age=' + (60 * 60 * 24 * days) + '; SameSite=Lax' + (location.protocol === 'https:' ? '; Secure' : '');
    } catch (e) {}
  }
  function setFlag(origin, surface) {
    try { localStorage.setItem(KEY, JSON.stringify({ t: Date.now(), stage: cfg.stage || '', o: origin || '', s: surface || '' })); } catch (e) {}
    setCookie(KEY, 90);
    tagCart();
  }
  function setPreviewFlag(ch) {
    try { localStorage.setItem(PKEY, JSON.stringify({ t: Date.now(), ch: ch })); } catch (e) {}
    setCookie(PKEY, 90);
    root.classList.add('bfcm-phone');
    tagCart();
  }

  /*
   * Order attribution: a device on the list tags its cart with the hidden attribute _bfcm_waitlist =
   * "<email|phone>|<surface>|<origin>|<utm_source or direct>|<stage at sign-up>", so every Shopify order
   * says whether (and how) the buyer joined. Sent once per cart value per session; no email or number.
   */
  var CART_KEY = 'rastah_bfcm_cart';
  function tagCart() {
    if (!unlocked() && !(function () { try { return !!localStorage.getItem(KEY); } catch (e) { return false; } })()) return;
    var j = {};
    try { j = JSON.parse(localStorage.getItem(KEY) || '{}') || {}; } catch (e) {}
    var clean = function (v) { return String(v || '').replace(/[|]/g, '/').slice(0, 40); };
    var v = [phoneGiven() ? 'phone' : 'email', clean(j.s) || 'unknown', clean(j.o) || 'unknown', clean(firstTouch().utm_source) || 'direct', clean(j.stage) || clean(cfg.stage)].join('|');
    try { if (sessionStorage.getItem(CART_KEY) === v) return; } catch (e) {}
    var rootPath = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    try {
      fetch(rootPath + 'cart/update.js', { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ attributes: { _bfcm_waitlist: v } }) })
        .then(function (r) { if (r.ok) { try { sessionStorage.setItem(CART_KEY, v); } catch (e) {} } })['catch'](function () {});
    } catch (e) {}
  }

  function revealPrices() {
    root.classList.add('bfcm-reveal');
    root.classList.add('bfcm-unlocked');
    setTimeout(function () { root.classList.remove('bfcm-reveal'); }, 1200);
  }

  // ---------------------------------------------------------------- surfaces
  /*
   * Waitlist surfaces in the page ([data-bfcm-surface]: the product-page box 'pdp', the
   * pre-sale page panel 'hub', and the embedded forms 'home', 'banner', 'footer', 'menu').
   * Every surface works the same way: one step shows at a time (data-bfcm-state: email |
   * phone | done | joined; CSS in snippets/bfcm-gate.liquid): email → step 2 (phone) → done,
   * or joined (+ the re-ask) for a device that joined earlier. The sheet keeps its own steps.
   * data-bfcm-after="hub" (home): after the email step, go to the pre-sale page, where step 2
   * waits (?bfcm_step2=<origin>, read pre-paint by the head snippet).
   */
  function surfaces() { return document.querySelectorAll('[data-bfcm-surface]'); }
  var RM = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  function setState(surf, st, animate) {
    if (!surf) return;
    // The pre-sale page panel (and any surface with data-bfcm-ease) eases to its new height
    // (e.g. collapsing into the success bar).
    var h0 = animate && !RM && (surf.hasAttribute('data-bfcm-wl') || surf.hasAttribute('data-bfcm-ease')) ? surf.offsetHeight : 0;
    surf.setAttribute('data-bfcm-state', st);
    var panel = surf.querySelector('[data-bfcm-step="' + st + '"]');
    if (animate && panel) { panel.classList.remove('bfcm-step-in'); void panel.offsetWidth; panel.classList.add('bfcm-step-in'); }
    if (h0) {
      var h1 = surf.offsetHeight;
      if (Math.abs(h1 - h0) > 4) {
        surf.style.height = h0 + 'px';
        surf.setAttribute('data-bfcm-anim', '');
        void surf.offsetHeight;
        surf.style.height = h1 + 'px';
        var end = function () { surf.style.height = ''; surf.removeAttribute('data-bfcm-anim'); surf.removeEventListener('transitionend', end); };
        surf.addEventListener('transitionend', end);
        setTimeout(end, 700);
      }
    }
    return panel;
  }
  function initSurfaces() {
    surfaces().forEach(function (sf) { if (!sf.hasAttribute('data-bfcm-state')) setState(sf, unlocked() ? 'joined' : 'email'); });
    watchSurfaceViews();
  }

  // The pre-sale page URL for data-bfcm-after="hub": Theme settings → Shop pre-sale link, else
  // the pre-sale collection on the hub template. '' when it is this page or another site.
  function hubUrl(origin) {
    var rootPath = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    var base = String(cfg.presaleUrl || '').trim() || rootPath + 'collections/bfcm-2026';
    // Already on a BFCM hub page (any tab): stay; step 2 opens here.
    if (document.querySelector('[data-bfcm-hub][data-bfcm-stage]')) return '';
    try {
      var u = new URL(base, location.href);
      if (u.origin !== location.origin || u.pathname === location.pathname) return '';
      u.searchParams.set('bfcm_step2', origin || 'home');
      return u.pathname + u.search + u.hash;
    } catch (e) { return ''; }
  }

  /*
   * Arrival with ?bfcm_step2=<origin> (the home form's redirect, or a welcome-email link with
   * ?bfcm_unlock=1&bfcm_step2=<origin>): on a joined device without a number, step 2 waits in the
   * pre-sale page's panel (pre-paint: html.bfcm-step2-arrive), or in the sheet when the panel
   * isn't showing. Joined devices with a number just see the joined bar.
   */
  function arrive() {
    var o = window.__bfcmStep2;
    window.__bfcmStep2 = '';
    if (o && GATE && unlocked() && !phoneGiven()) {
      var hp = q('[data-bfcm-step="phone"]') ? null : hubPanel();
      if (hp) {
        hp.__bfcmOrigin = o;
        setState(hp, 'phone');
        track('bfcm_gate_open', { gate_origin: o, gate_flow: 'sale', gate_step: 'phone', gate_surface: 'hub' });
      } else {
        openStep2(o);
      }
    }
    root.classList.remove('bfcm-step2-arrive');
  }
  // Form views for the always-visible surfaces (pre-sale panel, product-page box): one
  // bfcm_gate_view per surface per page, when half of it is on screen and prices are locked.
  // This is the denominator for the submit rate (email submits / form views) per surface.
  // Runs again after section re-renders: one observer, each surface observed once, and still one
  // view per surface name per page.
  var viewSeen = {}, viewIO = null;
  function watchSurfaceViews() {
    if (unlocked() || !('IntersectionObserver' in window)) return;
    if (!viewIO) {
      viewIO = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          var s = en.target.getAttribute('data-bfcm-surface');
          // Not counted while hidden (e.g. the menu surface inside a closed drawer).
          if (!en.isIntersecting || viewSeen[s] || unlocked() || en.target.offsetParent === null) return;
          if (window.getComputedStyle(en.target).visibility === 'hidden') return;
          viewSeen[s] = 1; viewIO.unobserve(en.target);
          track('bfcm_gate_view', { gate_flow: 'sale', gate_step: 'email', gate_surface: s });
        });
      }, { threshold: 0.5 });
    }
    surfaces().forEach(function (sf) {
      if (sf.__bfcmWatched) return;
      sf.__bfcmWatched = 1;
      viewIO.observe(sf);
    });
  }
  // The pre-sale page's waitlist panel, when it is showing (price-locked tab).
  function hubPanel() {
    var hp = document.querySelector('[data-bfcm-surface="hub"]');
    return hp && hp.offsetParent !== null ? hp : null;
  }
  /*
   * A locked price / Add to bag in the hub grid goes to the panel when some of it is on screen
   * (it is the form there); further down the grid the pop-up opens in place instead.
   */
  function hubPanelFor(el) {
    var hp = hubPanel();
    if (!hp || hp.getAttribute('data-bfcm-state') !== 'email' || !el.closest(HUB_GRID)) return null;
    var r = hp.getBoundingClientRect(), vh = window.innerHeight || root.clientHeight;
    return r.bottom > 80 && r.top < vh * 0.7 ? hp : null;
  }
  // The product-page box for this element's product (not quick view or another product's UI).
  function pdpBoxFor(el) {
    var box = document.querySelector('[data-bfcm-surface="pdp"]');
    if (!box || box.getAttribute('data-bfcm-state') !== 'email') return null;
    var prod = box.closest('[data-bfcm-lock]');
    if (el && prod && !prod.contains(el)) return null;
    return box;
  }
  /*
   * Bring a surface's email step into view and put the cursor in it, in the same tap (iPhone
   * keyboard). atc: a locked Add to bag is waiting (the Add to bag copy).
   */
  function openSurface(surf, origin, atc) {
    surf.__bfcmOrigin = origin || '';
    var panel = setState(surf, 'email');
    surf.classList.toggle('is-atc', !!atc);
    if (!panel) return;
    var r = panel.getBoundingClientRect(), vh = window.innerHeight || root.clientHeight;
    if (r.top < 64 || r.top > vh * 0.6) {
      try { panel.scrollIntoView({ block: 'center' }); } catch (e) { panel.scrollIntoView(); }
    }
    focusIn(panel);
    track('bfcm_gate_open', { gate_origin: origin || 'unknown', gate_flow: 'sale', gate_step: 'email', gate_surface: surf.getAttribute('data-bfcm-surface') });
  }

  // Success screen. false when the sheet has none (early access): callers fall back to a toast.
  function showDone(kind) {
    var step = q('[data-bfcm-step="done"]');
    if (!step) return false;
    step.setAttribute('data-kind', kind);
    var R = window.RastahBFCMRemind;
    if (R && typeof R.sync === 'function') { try { R.sync(); } catch (e) {} }
    afterDone = null;
    if (sheet.hidden) {
      lastFocus = document.activeElement;
      if (sheet.parentNode !== document.body) document.body.appendChild(sheet);
      sheet.hidden = false;
      root.classList.add('bfcm-sheet-open');
    }
    showStep('done', { animate: true });
    return true;
  }

  // "Added to bag at your pre-sale price" once the theme confirms the replayed add.
  function toastOnAdd(surf) {
    var off = false;
    function h() {
      if (off) return;
      off = true;
      document.removeEventListener('cart:item-added', h);
      if (surf) surf.classList.add('is-added');
      toast(cfg.toastAtc || 'Added to bag at your pre-sale price');
    }
    document.addEventListener('cart:item-added', h);
    setTimeout(function () { off = true; document.removeEventListener('cart:item-added', h); }, 8000);
  }

  // Run what the unlock interrupted: an Add to bag (replayed through the theme) or a require() callback.
  function resume(next, surf) {
    if (typeof next === 'function') {
      toastOnAdd(surf);
      setTimeout(function () { try { next(); } catch (e) { console.error(e); } }, 350);
    } else if (next && next.isConnected) {
      toastOnAdd(surf);
      setTimeout(function () { next.click(); }, 350);
    }
  }

  // What the success copy says (html[data-bfcm-done-v]): email | wa | sms_us | sms.
  function setDoneVariant(v) { root.setAttribute('data-bfcm-done-v', v); }

  // Step 2 skipped ("Not now", or the sheet closed on it): we'll email the link.
  function skipped(surface, how) {
    if (!root.hasAttribute('data-bfcm-done-v')) setDoneVariant('email');
    surfaces().forEach(function (sf) { if (sf.getAttribute('data-bfcm-state') === 'phone') setState(sf, 'done'); });
    track('bfcm_phone_skip', { gate_surface: surface, how: how });
  }

  /*
   * Email step done (sale flow). form: the form that unlocked (a surface's, or the sheet's).
   * Every other surface on the page shows the success state too.
   */
  function unlock(origin, form) {
    setFlag(origin || sheetOrigin, form ? (surfaceName(form) || 'sheet') : 'sheet');
    revealPrices();
    var next = pending;
    pending = null;
    var surf = form && form.closest('[data-bfcm-surface]');
    surfaces().forEach(function (sf) { if (sf !== surf) setState(sf, 'done'); });
    document.dispatchEvent(new CustomEvent('bfcm:unlock', { detail: { origin: origin } }));
    // Home form (data-bfcm-after="hub"): on to the pre-sale page, unlocked, where step 2 waits. The
    // Klaviyo request is already queued (keepalive, and retried from the queue if it is cut off).
    var to = surf && !next && surf.getAttribute('data-bfcm-after') === 'hub' ? hubUrl(origin) : '';
    if (to) {
      surf.setAttribute('data-bfcm-busy', '');
      setTimeout(function () { location.assign(to); }, 150);
      return;
    }
    if (surf && q('[data-bfcm-step="phone"]')) {
      // Step 2 is the drawer (the sheet's phone step) for every surface: the surface itself is done
      // (hidden once joined, no success bar), and a waiting Add to bag goes through when the drawer closes.
      surf.classList.remove('is-atc');
      setState(surf, 'done');
      if (openStep2(origin || surfOrigin(surf))) { resumeNext = next; return; }
    }
    if (surf) {
      // No drawer on this page (e.g. early access): step 2 in the same place, as before.
      surf.classList.remove('is-atc');
      var panel = setState(surf, surf.querySelector('[data-bfcm-step="phone"]') ? 'phone' : 'done', true);
      if (panel && !next) {
        if (panel.querySelector('input')) focusIn(panel);
        else { try { panel.focus({ preventScroll: true }); } catch (e) {} }
      }
      resume(next, surf);
      return;
    }
    // Joined from the navigation (menu card, desktop "Black Friday" item, badge, announcement): on to
    // the pre-sale page, unlocked, where the step 2 drawer waits (?bfcm_step2). On that page itself,
    // hubUrl() is '' and step 2 opens here.
    var navTo = !next && NAV_ORIGINS[sheetOrigin] ? hubUrl(sheetOrigin) : '';
    if (navTo) {
      var sb = q('[data-bfcm-step="email"] [data-bfcm-submit]');
      if (sb) sb.setAttribute('aria-busy', 'true');
      setTimeout(function () { location.assign(navTo); }, 150);
      return;
    }
    // The sheet: step 2 slides in; an Add to bag waits until the sheet closes (the bag drawer
    // would otherwise take the focus from the number field).
    if (q('[data-bfcm-step="phone"]')) {
      resumeNext = next;
      showStep('phone', { animate: true });
      return;
    }
    var done = !next && showDone('sale');
    if (!done) {
      closeSheet(false);
      if (!next) toast(cfg.toast || 'Pre-sale prices unlocked');
    }
    resume(next);
  }

  // ---------------------------------------------------------------- Klaviyo
  /*
   * Never on the critical path: the shopper is unlocked first, then the request goes out
   * in the background (fetch keepalive, so it survives a navigation). Every request is
   * written to a small localStorage queue before it is sent and removed on success, so a
   * request lost to a closed tab or a dropped connection is retried on the next page view
   * (and when the browser comes back online). Klaviyo rejections (4xx other than 429) are
   * dropped; anything else is retried up to 5 times within 7 days. Subscribing twice is
   * harmless on Klaviyo's side, so a rare duplicate send is fine.
   */
  var QKEY = 'rastah_bfcm_q';
  var Q_MAX_TRIES = 5;
  var Q_MAX_AGE = 7 * 864e5;
  var inflight = {};

  function qLoad() {
    try { var a = JSON.parse(localStorage.getItem(QKEY) || '[]'); return Array.isArray(a) ? a : []; } catch (e) { return []; }
  }
  function qSave(a) {
    try { if (a.length) localStorage.setItem(QKEY, JSON.stringify(a.slice(-12))); else localStorage.removeItem(QKEY); } catch (e) {}
  }
  function qUpdate(id, fn) {
    var a = qLoad(), out = [];
    for (var i = 0; i < a.length; i++) {
      var j = a[i];
      if (j && j.id === id) { j = fn(j); if (!j) continue; }
      if (j) out.push(j);
    }
    qSave(out);
  }

  function post(path, body) {
    var url = 'https://a.klaviyo.com/client/' + path + '/?company_id=' + encodeURIComponent(cfg.siteKey || '');
    var opts = {
      method: 'POST',
      headers: { 'Content-Type': 'application/vnd.api+json', Accept: 'application/vnd.api+json', revision: REVISION },
      body: JSON.stringify(body),
      keepalive: true
    };
    var ctrl = typeof AbortController === 'function' ? new AbortController() : null;
    if (ctrl) opts.signal = ctrl.signal;
    var timer = setTimeout(function () { ctrl && ctrl.abort(); }, 10000);
    var p;
    try { p = fetch(url, opts); } catch (e) { p = Promise.reject(e); }
    return p.catch(function (err) {
      // Some browsers refuse keepalive on a request that needs a CORS preflight: send it plain.
      if (err && err.name === 'TypeError') { opts.keepalive = false; return fetch(url, opts); }
      throw err;
    }).then(function (r) {
      clearTimeout(timer);
      if (r.ok) return true;
      var e = new Error(path + ' HTTP ' + r.status);
      e.fatal = r.status >= 400 && r.status < 500 && r.status !== 429;
      throw e;
    }, function (err) { clearTimeout(timer); throw err; });
  }

  function send(job) {
    if (!job || inflight[job.id]) return;
    inflight[job.id] = 1;
    post(job.path, job.body).then(function () {
      delete inflight[job.id];
      qUpdate(job.id, function () { return null; });
      if (job.identify) {
        // Tie this browser to the profile for onsite attribution (Klaviyo JS, if loaded).
        try { window.klaviyo && typeof window.klaviyo.identify === 'function' && window.klaviyo.identify({ email: job.identify }); } catch (e) {}
      }
    }, function (err) {
      delete inflight[job.id];
      console.warn('[bfcm-gate] Klaviyo request failed' + (err && err.fatal ? ' (dropped)' : ' (will retry)') + '.', err);
      qUpdate(job.id, function (j) {
        j.n = (j.n || 0) + 1;
        return err && err.fatal || j.n >= Q_MAX_TRIES ? null : j;
      });
    });
  }

  // Write the job to the queue, then send it now. Returns at once.
  function enqueue(path, body, identify) {
    var job = { id: Date.now().toString(36) + Math.random().toString(36).slice(2, 7), path: path, body: body, t: Date.now(), n: 0 };
    if (identify) job.identify = identify;
    var a = qLoad();
    a.push(job);
    qSave(a);
    send(job);
  }

  // Retry what an earlier page view left behind (after the page has settled).
  function flushQueue() {
    var now = Date.now(), a = qLoad(), keep = [];
    for (var i = 0; i < a.length; i++) {
      if (a[i] && a[i].path && a[i].body && now - (a[i].t || 0) < Q_MAX_AGE) keep.push(a[i]);
    }
    if (keep.length !== a.length) qSave(keep);
    keep.forEach(send);
  }

  // Klaviyo onsite JS's exchange id (only if klaviyo.js identified this browser).
  function klaviyoKx() {
    try {
      var m = document.cookie.match(/(?:^|;\s*)__kla_id=([^;]+)/);
      if (!m) return '';
      return JSON.parse(atob(decodeURIComponent(m[1])))['$exchange_id'] || '';
    } catch (e) { return ''; }
  }

  /*
   * Klaviyo client events: "Joined BFCM Waitlist" (email step) and "Added Phone to BFCM Waitlist"
   * (phone step), for flows and reporting by surface / origin. Same queue and retries as the
   * subscriptions; unique_id makes a retried send count once. Never in QA mode (no list ID).
   */
  function eventProps(surface, origin, ch, cc) {
    var ft = firstTouch(), o = {
      surface: surface || '',
      origin: origin || '',
      channel: ch || '',
      phone_country: cc || '',
      stage: cfg.stage || '',
      page_path: location.pathname,
      landing_path: ft.landing || ''
    };
    UTM.forEach(function (k) { o[k] = ft[k] || ''; });
    return o;
  }
  function kEvent(name, profile, props) {
    enqueue('events', {
      data: {
        type: 'event',
        attributes: {
          properties: props,
          time: new Date().toISOString(),
          unique_id: 'bfcm26-' + Date.now().toString(36) + Math.random().toString(36).slice(2, 8),
          metric: { data: { type: 'metric', attributes: { name: name } } },
          profile: { data: { type: 'profile', attributes: profile } }
        }
      }
    });
  }

  /*
   * Klaviyo onsite JS (window.klaviyo, the app embed): waits up to 20 s for it, then cb(klaviyo).
   * Its isIdentified() resolves true for a browser Klaviyo knows (a click from a Klaviyo email or
   * SMS, an earlier form or sign-up).
   */
  function whenKlaviyo(cb) {
    var n = 0;
    (function poll() {
      var k = window.klaviyo;
      if (k && typeof k.isIdentified === 'function' && typeof k.track === 'function') { try { cb(k); } catch (e) {} return; }
      if (++n < 80) setTimeout(poll, 250);
    })();
  }
  // Once per browser session (sessionStorage). false when storage is unavailable (not sent then).
  function firstInSession(key) {
    try {
      if (sessionStorage.getItem(key)) return false;
      sessionStorage.setItem(key, String(Date.now()));
      return true;
    } catch (e) { return false; }
  }
  // klaviyo.track for an identified browser, once per session; never in QA mode.
  function trackKnown(name, props, key) {
    if (!cfg.listId) { console.info('[bfcm-gate] QA mode: would send "' + name + '" to Klaviyo for an identified browser.'); return; }
    try { if (sessionStorage.getItem(key)) return; } catch (e) { return; }
    whenKlaviyo(function (k) {
      Promise.resolve(k.isIdentified()).then(function (known) {
        if (known && firstInSession(key)) return k.track(name, props);
      })['catch'](function () {});
    });
  }

  // Existing subscribers on the drop: a Preview collection's page or one of its products.
  function viewedDrop() {
    if (cfg.dropView) trackKnown('Viewed BFCM Drop', { collection: cfg.dropView, stage: cfg.stage || '' }, 'rastah_bfcm_vdrop');
  }

  /*
   * Known subscribers (Theme settings → BFCM 2026 → "Unlock prices for known subscribers", off by
   * default; pre-sale and preview week). Logged-in customers who accept email marketing arrive
   * unlocked from the server (html.bfcm-unlocked bfcm-known). A browser Klaviyo has identified is
   * unlocked here once klaviyo.isIdentified() resolves true, and stays unlocked on later pages
   * (rastah_bfcm_known, read pre-paint; prices only, never early access). No subscription is sent.
   * Identified browsers send klaviyo.track('Viewed BFCM Pre-sale') once per session.
   */
  function autoKnown() {
    if (!cfg.autoKnown || !GATE) return;
    if (cfg.knownCustomer && firstInSession('rastah_bfcm_auto_c')) track('bfcm_auto_unlock', { gate_trigger: 'customer' });
    whenKlaviyo(function (k) {
      Promise.resolve(k.isIdentified()).then(function (known) {
        if (!known) return;
        if (!unlocked()) {
          try { localStorage.setItem(KNOWN_KEY, JSON.stringify({ t: Date.now(), via: 'klaviyo' })); } catch (e) {}
          root.classList.add('bfcm-known');
          revealPrices();
          surfaces().forEach(function (sf) { var s0 = sf.getAttribute('data-bfcm-state'); if (!s0 || s0 === 'email') setState(sf, 'joined'); });
          document.dispatchEvent(new CustomEvent('bfcm:unlock', { detail: { origin: 'klaviyo_known', auto: true } }));
          track('bfcm_auto_unlock', { gate_trigger: 'klaviyo' });
        }
        trackKnown('Viewed BFCM Pre-sale', { stage: cfg.stage || '', page_path: location.pathname }, 'rastah_bfcm_vpre');
      })['catch'](function () {});
    });
  }

  /*
   * Klaviyo pop-up bridge (Theme settings → BFCM 2026 → Klaviyo pop-up form IDs). The team's BFCM
   * pop-ups are built in Klaviyo, which subscribes the shopper itself; this only mirrors the
   * result on the device. Klaviyo's window "klaviyoForms" events, for a listed formId:
   *   submit                  → prices unlocked on this device (rastah_bfcm_unlock), surfaces on
   *                             done, bfcm:unlock, dataLayer bfcm_unlock_email;
   *   submit / stepSubmit with a phone number in the step → also the number flag
   *                             (rastah_bfcm_preview: html.bfcm-phone, preview open), bfcm:unlock-phone,
   *                             dataLayer bfcm_unlock_phone.
   * Both pushes carry gate_surface 'klaviyo_popup' and gate_origin = the form ID, once per form
   * per page. The form's values (email, number) are never read into the dataLayer.
   */
  var POPUP_IDS = (Array.isArray(cfg.popupForms) ? cfg.popupForms : []).map(function (x) { return String(x || '').trim(); }).filter(Boolean);
  var popupSeen = {};
  function mdHas(md, test) {
    for (var k in md) {
      if (Object.prototype.hasOwnProperty.call(md, k) && test(k.toLowerCase()) && md[k] !== '' && md[k] != null && md[k] !== false) return true;
    }
    return false;
  }
  function popupUnlock(id) {
    if (!unlocked()) {
      setFlag(id || 'klaviyo_popup', 'klaviyo_popup');
      revealPrices();
    }
    surfaces().forEach(function (sf) { var s0 = sf.getAttribute('data-bfcm-state'); if (!s0 || s0 === 'email') setState(sf, 'done'); });
    document.dispatchEvent(new CustomEvent('bfcm:unlock', { detail: { origin: id, surface: 'klaviyo_popup' } }));
  }
  function onKlaviyoForm(e) {
    var d = (e && e.detail) || {};
    if (d.type !== 'submit' && d.type !== 'stepSubmit') return;
    var id = String(d.formId || '');
    if (!id || POPUP_IDS.indexOf(id) < 0) return;
    var md = d.metaData && typeof d.metaData === 'object' ? d.metaData : {};
    var hasPhone = mdHas(md, function (k) { return /phone|whatsapp/.test(k) && !/consent|opt_?in|source/.test(k); });
    var hasEmail = mdHas(md, function (k) { return k === '$email' || k === 'email'; });
    if (d.type === 'submit' && !popupSeen[id + ':e']) {
      popupSeen[id + ':e'] = 1;
      popupUnlock(id);
      if (hasEmail || !hasPhone) {
        track('bfcm_unlock_email', Object.assign({
          gate_origin: id, gate_flow: 'sale', gate_trigger: 'klaviyo_popup', gate_surface: 'klaviyo_popup',
          klaviyo_result: 'klaviyo_form', hub_tab: hubTab()
        }, utmFields()));
      }
    }
    if (hasPhone && !popupSeen[id + ':p']) {
      popupSeen[id + ':p'] = 1;
      if (!popupSeen[id + ':e']) popupUnlock(id);
      var cc = cfg.country || '';
      var ch = mdHas(md, function (k) { return /whatsapp/.test(k); }) ? 'whatsapp' : defaultChannel(cc);
      setPreviewFlag(ch);
      root.classList.toggle('bfcm-ch-sms', ch === 'sms');
      root.classList.add('bfcm-preview-open', 'bfcm-phone');
      setDoneVariant(ch === 'sms' ? (cc === 'US' || cc === 'CA' ? 'sms_us' : 'sms') : 'wa');
      if (PREVIEW && !root.classList.contains('bfcm-preview-pdp') && window.__bfcmReveal) window.__bfcmReveal(document, true);
      surfaces().forEach(function (sf) { if (sf.getAttribute('data-bfcm-state') === 'phone') setState(sf, 'done'); });
      document.dispatchEvent(new CustomEvent('bfcm:unlock-phone', { detail: { channel: ch, surface: 'klaviyo_popup' } }));
      track('bfcm_unlock_phone', Object.assign({
        gate_origin: id, gate_flow: 'sale', gate_surface: 'klaviyo_popup', phone_channel: ch, phone_country: cc,
        gate_steps: 2, klaviyo_result: 'klaviyo_form', hub_tab: hubTab()
      }, utmFields()));
    }
  }
  if (POPUP_IDS.length) window.addEventListener('klaviyoForms', onKlaviyoForm);

  // Email step: queued in the background. Returns 'queued', or 'qa' when no list is set.
  function subscribe(email, surface, origin) {
    if (!cfg.listId) {
      console.info('[bfcm-gate] QA mode (no Klaviyo list ID set): unlocking without subscribing.');
      return 'qa';
    }
    enqueue('subscriptions', {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: cfg.source || 'BFCM 2026 pre-sale gate',
          profile: { data: { type: 'profile', attributes: {
            email: email,
            subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } }
          } } }
        },
        relationships: { list: { data: { type: 'list', id: cfg.listId } } }
      }
    }, email);
    // Where the sign-up came from, on the profile (first touch + the surface).
    var props = attribProps(surface);
    props.bfcm_joined_at = new Date().toISOString();
    if (origin) props.bfcm_gate_origin = origin;
    enqueue('profiles', { data: { type: 'profile', attributes: { email: email, properties: props } } });
    // channel: the step 2 this shopper is offered (SMS in the US, Canada, Australia; WhatsApp elsewhere).
    kEvent('Joined BFCM Waitlist', { email: email }, eventProps(surface, origin, channel, ''));
    return 'queued';
  }

  /*
   * Phone step. SMS: subscription with SMS marketing consent to the SMS list,
   * then profile properties. WhatsApp: consent captured as profile properties
   * only (no Klaviyo SMS consent: the team messages on WhatsApp in-house).
   * Identifier: the email from step 1 when we have it, else Klaviyo's cookie
   * id, else the phone number itself. Queued in the background like the email.
   */
  function subscribePhone(e164, ch, cc, flowName, surface, origin) {
    if (!cfg.phoneListId) {
      console.info('[bfcm-gate] QA mode (no Klaviyo SMS list ID set): not sending the number.');
      return 'qa';
    }
    var now = new Date().toISOString();
    var pv = flowName === 'preview';
    var source = pv ? (cfg.phoneSource || 'BFCM 2026 preview gate') : 'BFCM 2026 pre-sale gate (step 2)';
    // Preview flow: the bfcm26_preview_* properties as before; pre-sale step 2: bfcm26_phone_*.
    var pre = pv ? 'bfcm26_preview_' : 'bfcm26_phone_';
    var props = attribProps();
    props.bfcm_phone_surface = surface || '';
    if (origin) props.bfcm_phone_origin = origin;
    props[pre + 'optin'] = true;
    props[pre + 'channel'] = ch;
    props[pre + 'optin_at'] = now;
    props[pre + 'source'] = source;
    props[pre + 'country'] = cc;
    var ids = {};
    if (lastEmail) ids.email = lastEmail;
    if (ch === 'sms') {
      var sub = { phone_number: e164, subscriptions: { sms: { marketing: { consent: 'SUBSCRIBED' } } } };
      if (lastEmail) sub.email = lastEmail;
      enqueue('subscriptions', {
        data: {
          type: 'subscription',
          attributes: { custom_source: source, profile: { data: { type: 'profile', attributes: sub } } },
          relationships: { list: { data: { type: 'list', id: cfg.phoneListId } } }
        }
      });
      if (!lastEmail) ids.phone_number = e164;
    } else {
      props.whatsapp_opt_in = true;
      props.whatsapp_number = e164;
      props.whatsapp_opt_in_at = now;
      props.whatsapp_opt_in_source = source;
      props.whatsapp_consent_text = cfg.waConsent || '';
      if (!lastEmail) { var kx = klaviyoKx(); if (kx) ids._kx = kx; else ids.phone_number = e164; }
    }
    // The event goes to the same profile as the properties (email, else the number or Klaviyo's id).
    var who = Object.assign({}, ids);
    enqueue('profiles', { data: { type: 'profile', attributes: Object.assign(ids, { properties: props }) } });
    kEvent('Added Phone to BFCM Waitlist', who, eventProps(surface, origin, ch, cc));
    return 'queued';
  }

  // ---------------------------------------------------------------- email step
  // name@domain.tld, the domain made of non-empty labels and a 2+ letter TLD.
  function validEmail(v) { return /^[^\s@]+@[^\s@.]+(\.[^\s@.]+)*\.[a-z]{2,}$/i.test(v); }
  var BAD_EMAIL = 'Check your email: it needs an @ and a domain, like name@gmail.com';
  function emptyEmail(form) {
    return form.closest('[data-bfcm-surface="pdp"]') ? 'Enter your email to unlock your price.' : 'Enter your email to unlock your prices.';
  }

  /*
   * Typo suggestion ("Did you mean ali@gmail.com? Use it"): common webmail domains one or two
   * edits away (Damerau-Levenshtein), a mistyped .com, a missing @ before a known domain.
   * Checked when the field is left and on the first submit, never while typing. A second
   * tap on the button keeps what the shopper typed.
   */
  var DOMAINS = ['gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'icloud.com', 'live.com', 'yahoo.co.uk', 'hotmail.co.uk',
                 'googlemail.com', 'me.com', 'msn.com', 'aol.com', 'protonmail.com', 'ymail.com'];
  function lev(a, b) {
    var m = a.length, n = b.length, d = [], i, j;
    for (i = 0; i <= m; i++) d[i] = [i];
    for (j = 0; j <= n; j++) d[0][j] = j;
    for (i = 1; i <= m; i++) {
      for (j = 1; j <= n; j++) {
        d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
        if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) d[i][j] = Math.min(d[i][j], d[i - 2][j - 2] + 1);
      }
    }
    return d[m][n];
  }
  function suggest(v) {
    v = String(v || '').trim().toLowerCase();
    if (!v) return '';
    var at = v.lastIndexOf('@'), i, d;
    if (at < 0) {
      for (i = 0; i < DOMAINS.length; i++) {
        d = DOMAINS[i];
        if (v.length > d.length + 1 && v.slice(-(d.length + 1)) === '.' + d) return v.slice(0, -(d.length + 1)) + '@' + d;
        if (v.length > d.length && v.slice(-d.length) === d) return v.slice(0, -d.length) + '@' + d;
      }
      return '';
    }
    var local = v.slice(0, at), dom = v.slice(at + 1);
    if (!local || !dom || DOMAINS.indexOf(dom) > -1) return '';
    var fixed = dom.replace(/\.(con|cmo|cpm|vom|comm|coom|om|cm)$/, '.com');
    if (dom.indexOf('.') < 0 && DOMAINS.indexOf(dom + '.com') > -1) return local + '@' + dom + '.com';
    if (fixed !== dom && DOMAINS.indexOf(fixed) > -1) return local + '@' + fixed;
    var best = '', bd = 9;
    DOMAINS.forEach(function (k) { var x = lev(dom, k); if (x < bd) { bd = x; best = k; } });
    if (bd > 0 && (bd === 1 || (bd === 2 && dom.length >= 7))) return local + '@' + best;
    if (fixed !== dom) return local + '@' + fixed;
    return '';
  }
  function esc(t) { return String(t).replace(/[&<>"']/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; }); }
  function showSug(form, val) {
    var box = form.querySelector('[data-bfcm-sug]');
    if (!box) return;
    if (!val) { box.hidden = true; box.removeAttribute('data-val'); return; }
    var at = val.lastIndexOf('@');
    box.querySelector('[data-bfcm-sug-text]').innerHTML = 'Did you mean ' + esc(val.slice(0, at + 1)) + '<b>' + esc(val.slice(at + 1)) + '</b>?';
    box.setAttribute('data-val', val);
    box.hidden = false;
  }

  // Error under a field (the phone's border is its wrapper). focus: false leaves focus alone.
  function fieldError(form, input, msg, focus) {
    var err = form.querySelector('[data-bfcm-error]');
    var box = input.closest('[data-bfcm-tel]') || input;
    if (msg) {
      err.textContent = msg;
      err.hidden = false;
      box.setAttribute('aria-invalid', 'true');
      if (focus !== false) input.focus();
    } else {
      err.hidden = true;
      err.textContent = '';
      box.removeAttribute('aria-invalid');
    }
  }

  // Valid email: unlock on the spot (flag, prices, next step), then subscribe in the background.
  // keep: the shopper chose their own spelling (second tap) or the suggestion (Use it).
  function onSubmit(form, keep) {
    var input = form.querySelector('input[type="email"]');
    var email = (input.value || '').trim();
    clearTimeout(form.__bfcmBlur);
    if (!email) { showSug(form, ''); return fieldError(form, input, emptyEmail(form)); }
    var sug = suggest(email);
    if (!validEmail(email)) {
      showSug(form, sug);
      if (sug) track('bfcm_typo_suggest', { gate_origin: formOrigin(form) });
      return fieldError(form, input, BAD_EMAIL);
    }
    if (sug && !keep && form.__bfcmTypoFor !== email) {
      form.__bfcmTypoFor = email;
      fieldError(form, input, null);
      showSug(form, sug);
      track('bfcm_typo_suggest', { gate_origin: formOrigin(form) });
      return;
    }
    fieldError(form, input, null);
    showSug(form, '');
    form.__bfcmTypoFor = '';
    var origin = formOrigin(form);
    var inPreview = flow === 'preview' && sheet && !sheet.hidden && form.closest('[data-bfcm-sheet]');
    var trigger = pending ? 'add_to_cart' : origin;
    input.value = '';
    lastEmail = email;
    try { sessionStorage.setItem(EM_KEY, email); } catch (e) {}
    var surface = surfaceName(form);
    var gOrigin = inPreview ? origin0 : origin;
    var result = subscribe(email, surface, gOrigin);
    track('bfcm_unlock_email', Object.assign({
      gate_origin: gOrigin,
      gate_flow: inPreview ? 'preview' : 'sale',
      gate_trigger: trigger,
      gate_surface: surface,
      klaviyo_result: result,
      hub_tab: hubTab()
    }, utmFields()));
    if (inPreview) {
      // Step 1 done: pricing unlocks behind the sheet, the phone step slides in.
      setFlag(origin0 || 'preview', surface || 'sheet');
      revealPrices();
      // The page's waitlist surfaces (e.g. the pre-sale tab's panel, hidden on the Preview tab) show joined.
      surfaces().forEach(function (sf) { setState(sf, 'done'); });
      document.dispatchEvent(new CustomEvent('bfcm:unlock', { detail: { origin: 'preview' } }));
      justEmail = true;
      showStep('phone', { two: true, justEmail: true, animate: true });
    } else {
      unlock(origin, form);
    }
  }

  // Leaving the field: a beat later (so a tap on the button is handled first and nothing
  // jumps under the finger), flag a bad address or offer the fix. Never while typing.
  document.addEventListener('focusout', function (e) {
    var inp = e.target;
    if (!inp || !inp.matches || !inp.matches('[data-bfcm-form] input[type="email"]')) return;
    var form = inp.closest('form');
    clearTimeout(form.__bfcmBlur);
    form.__bfcmBlur = setTimeout(function () {
      if (!inp.isConnected || document.activeElement === inp) return;
      var v = (inp.value || '').trim();
      if (!v) return;
      var sug = suggest(v);
      if (!validEmail(v)) { fieldError(form, inp, BAD_EMAIL, false); showSug(form, sug); return; }
      if (sug && form.__bfcmTypoFor !== v) {
        form.__bfcmTypoFor = v;
        showSug(form, sug);
        track('bfcm_typo_suggest', { gate_origin: formOrigin(form) });
      }
    }, 220);
  });
  document.addEventListener('input', function (e) {
    var inp = e.target, form = inp && inp.closest && inp.closest('[data-bfcm-form], [data-bfcm-phone-form]');
    if (!form) return;
    var err = form.querySelector('[data-bfcm-error]');
    if (inp.type === 'email') {
      var v = (inp.value || '').trim();
      if (err && !err.hidden && validEmail(v)) fieldError(form, inp, null);
      var box = form.querySelector('[data-bfcm-sug]');
      if (box && !box.hidden && suggest(v) !== box.getAttribute('data-val')) showSug(form, '');
    } else if (inp.type === 'tel') {
      var cc = (form.querySelector('[data-bfcm-cc]') || {}).value || cfg.country || '';
      if (err && !err.hidden && toE164(inp.value, cc)) fieldError(form, inp, null);
    }
  });

  // ---------------------------------------------------------------- phone step
  var DIAL = ('AD376 AE971 AF93 AG1 AI1 AL355 AM374 AO244 AR54 AS1 AT43 AU61 AW297 AX358 AZ994 BA387 BB1 BD880 BE32 BF226 BG359 BH973 BI257 BJ229 BL590 BM1 BN673 BO591 BQ599 BR55 BS1 BT975 BW267 BY375 BZ501 ' +
    'CA1 CC61 CD243 CF236 CG242 CH41 CI225 CK682 CL56 CM237 CN86 CO57 CR506 CU53 CV238 CW599 CX61 CY357 CZ420 DE49 DJ253 DK45 DM1 DO1 DZ213 EC593 EE372 EG20 EH212 ER291 ES34 ET251 FI358 FJ679 FK500 FM691 FO298 FR33 ' +
    'GA241 GB44 GD1 GE995 GF594 GG44 GH233 GI350 GL299 GM220 GN224 GP590 GQ240 GR30 GT502 GU1 GW245 GY592 HK852 HN504 HR385 HT509 HU36 ID62 IE353 IL972 IM44 IN91 IO246 IQ964 IR98 IS354 IT39 JE44 JM1 JO962 JP81 ' +
    'KE254 KG996 KH855 KI686 KM269 KN1 KR82 KW965 KY1 KZ7 LA856 LB961 LC1 LI423 LK94 LR231 LS266 LT370 LU352 LV371 LY218 MA212 MC377 MD373 ME382 MF590 MG261 MH692 MK389 ML223 MM95 MN976 MO853 MP1 MQ596 MR222 MS1 MT356 MU230 MV960 MW265 MX52 MY60 MZ258 ' +
    'NA264 NC687 NE227 NF672 NG234 NI505 NL31 NO47 NP977 NR674 NU683 NZ64 OM968 PA507 PE51 PF689 PG675 PH63 PK92 PL48 PM508 PN64 PR1 PS970 PT351 PW680 PY595 QA974 RE262 RO40 RS381 RU7 RW250 ' +
    'SA966 SB677 SC248 SD249 SE46 SG65 SH290 SI386 SJ47 SK421 SL232 SM378 SN221 SO252 SR597 SS211 ST239 SV503 SX1 SY963 SZ268 TC1 TD235 TG228 TH66 TJ992 TK690 TL670 TM993 TN216 TO676 TR90 TT1 TV688 TW886 TZ255 ' +
    'UA380 UG256 US1 UY598 UZ998 VA39 VC1 VE58 VG1 VI1 VN84 VU678 WF681 WS685 XK383 YE967 YT262 ZA27 ZM260 ZW263').split(' ').reduce(function (m, s) { m[s.slice(0, 2)] = s.slice(2); return m; }, {});

  /*
   * Main markets: national number rules and the example shown in the placeholder and the
   * error. trunk: a leading 0 is the trunk prefix (0300 → 300). Other countries: any
   * 6+ digit national number that fits E.164.
   */
  var NANP = /^[2-9]\d{2}[2-9]\d{6}$/;
  var RULES = {
    PK: { trunk: true, re: /^3\d{9}$/, ex: '300 1234567' },
    AE: { trunk: true, re: /^5\d{8}$/, ex: '50 123 4567' },
    US: { trunk: false, re: NANP, ex: '(201) 555-0123' },
    CA: { trunk: false, re: NANP, ex: '(204) 555-0123' },
    GB: { trunk: true, re: /^7\d{9}$/, ex: '7400 123456' },
    AU: { trunk: true, re: /^4\d{8}$/, ex: '412 345 678' }
  };
  function smsOk(cc) { return /^(US|CA|AU)$/.test(cc || ''); }

  var channel = 'whatsapp';
  var chanTouched = false;
  function defaultChannel(cc) { return smsOk(cc) ? 'sms' : 'whatsapp'; }

  // Channel copy (label, button, consent, switch link) follows html.bfcm-ch-sms through CSS.
  function setChannel(ch) {
    channel = ch;
    root.classList.toggle('bfcm-ch-sms', ch === 'sms');
    syncPhoneForms();
  }
  function syncPhoneForms() {
    document.querySelectorAll('[data-bfcm-phone-form]').forEach(function (form) {
      var sel = form.querySelector('[data-bfcm-cc]');
      var cc = (sel && sel.value) || cfg.country || '';
      var input = form.querySelector('input[type="tel"]');
      var r = RULES[cc];
      if (input) input.setAttribute('placeholder', r ? r.ex : (channel === 'sms' ? 'Mobile number' : 'WhatsApp number'));
      var links = form.nextElementSibling;
      var sw = links && links.querySelector('[data-bfcm-chan-switch]');
      if (sw) sw.hidden = !smsOk(cc);
    });
  }

  function initPhone() {
    var sels = document.querySelectorAll('[data-bfcm-cc]');
    if (!sels.length) return;
    Array.prototype.forEach.call(sels, function (sel) {
      Array.prototype.forEach.call(sel.options, function (o) {
        var d = DIAL[o.value];
        if (d) o.textContent = o.value + ' +' + d;
      });
      if (!sel.value && cfg.country) sel.value = cfg.country;
      sel.addEventListener('change', function () {
        Array.prototype.forEach.call(document.querySelectorAll('[data-bfcm-cc]'), function (o) { if (o !== sel) o.value = sel.value; });
        if (!chanTouched || !smsOk(sel.value)) setChannel(defaultChannel(sel.value));
        else syncPhoneForms();
      });
    });
    setChannel(defaultChannel(sels[0].value || cfg.country));
  }

  /*
   * Any way of typing a number → E.164, or '' if it can't be a mobile number there:
   * 0300-1234567, 300 1234567, +92 300 1234567, 0092 300…, 92300… (code without the +),
   * (201) 555-0123, 1 201… International input (+ or 00) is read against its own code,
   * so a +44 number typed under PK still works.
   */
  function toE164(raw, cc) {
    var s = String(raw || '').replace(/[^\d+]/g, '');
    var intl = false;
    if (s.charAt(0) === '+') { s = s.slice(1); intl = true; }
    else if (s.slice(0, 2) === '00') { s = s.slice(2); intl = true; }
    s = s.replace(/\+/g, '');
    if (!s) return '';
    var dial = DIAL[cc] || '';
    var at = cc;
    if (intl) {
      if (!(dial && s.indexOf(dial) === 0)) {
        at = '';
        Object.keys(RULES).forEach(function (k) { if (!at && s.indexOf(DIAL[k]) === 0) at = k; });
        if (!at) return s.length >= 8 && s.length <= 15 ? '+' + s : '';
      }
      dial = DIAL[at];
      s = s.slice(dial.length);
    } else {
      if (!dial) return '';
      if (dial === '1' && s.length === 11 && s.charAt(0) === '1') s = s.slice(1);
      else if (dial !== '1' && s.indexOf(dial) === 0 && s.length > dial.length + 8) s = s.slice(dial.length);
    }
    var r = RULES[at];
    if (r) {
      if (r.trunk && s.charAt(0) === '0') s = s.slice(1);
      return r.re.test(s) ? '+' + dial + s : '';
    }
    s = s.replace(/^0+/, '');
    return s.length >= 6 && dial.length + s.length <= 15 ? '+' + dial + s : '';
  }

  function phoneErr(cc) {
    var r = RULES[cc];
    if (channel !== 'sms') return r ? 'Enter your WhatsApp number, e.g. ' + r.ex : 'Enter your WhatsApp number.';
    if (cc === 'US' || cc === 'CA') return 'Enter a 10-digit mobile number, e.g. ' + r.ex;
    return r ? 'Enter your mobile number, e.g. ' + r.ex : 'Enter your mobile number.';
  }

  function onPhoneSubmit(form) {
    var input = form.querySelector('input[type="tel"]');
    var cc = (form.querySelector('[data-bfcm-cc]') || {}).value || cfg.country || '';
    var e164 = toE164(input.value, cc);
    if (!e164) return fieldError(form, input, phoneErr(cc));
    fieldError(form, input, null);
    var ch = channel;
    var surf = form.closest('[data-bfcm-surface]');
    var inPreview = !surf && flow === 'preview';
    var surface = surfaceName(form);
    var pOrigin = inPreview ? origin0 : (surf ? surfOrigin(surf) : sheetOrigin);
    var result = subscribePhone(e164, ch, cc, inPreview ? 'preview' : 'presale', surface, pOrigin);
    track('bfcm_unlock_phone', Object.assign({
      gate_origin: pOrigin,
      gate_flow: inPreview ? 'preview' : 'sale',
      gate_surface: surface,
      phone_channel: ch,
      phone_country: cc,
      gate_steps: inPreview && !justEmail ? 1 : 2,
      klaviyo_result: result,
      hub_tab: hubTab()
    }, utmFields()));
    input.value = '';
    // This device gave a number: the same flag as the preview's phone step (so the drop opens in
    // preview week too), and the success copy for this channel.
    setPreviewFlag(ch);
    root.classList.toggle('bfcm-ch-sms', ch === 'sms');
    root.classList.add('bfcm-preview-open', 'bfcm-phone');
    setDoneVariant(ch === 'sms' ? (cc === 'US' || cc === 'CA' ? 'sms_us' : 'sms') : 'wa');
    if (inPreview) { finishPreview(ch); return; }
    document.dispatchEvent(new CustomEvent('bfcm:unlock-phone', { detail: { channel: ch } }));
    // Preview week: the drop's photos sharpen in (not on a drop product page, which needs a reload).
    if (PREVIEW && !root.classList.contains('bfcm-preview-pdp') && window.__bfcmReveal) window.__bfcmReveal(document, true);
    surfaces().forEach(function (sf) { if (sf !== surf && sf.getAttribute('data-bfcm-state') === 'phone') setState(sf, 'done'); });
    if (surf) {
      var panel = setState(surf, 'done', true);
      if (panel) { try { panel.focus({ preventScroll: true }); } catch (e) {} }
      return;
    }
    // Sale flow in the sheet: the success screen, or straight to the waiting Add to bag.
    if (resumeNext) closeSheet(false);
    else showDone('sale');
  }

  function finishPreview(ch) {
    var msg = 'You’re in. Early-access link arrives ' + (ch === 'sms' ? 'by text' : 'on WhatsApp') + ', ' + (launchAt() || '1 hour before everyone') + '.';
    // Success screen first (Add to calendar); the photos sharpen in when it closes.
    var done = showDone('preview');
    if (done) afterDone = function () { revealPreview(null); };
    else closeSheet(false);
    document.dispatchEvent(new CustomEvent('bfcm:unlock-phone', { detail: { channel: ch } }));
    if (!done) revealPreview(msg);
  }

  function revealPreview(msg) {
    if (root.classList.contains('bfcm-preview-pdp')) {
      // The product gallery (Swiper, lightbox) initialised on the stand-ins:
      // reload so the real one boots; the head check reveals it before paint.
      if (msg) { try { sessionStorage.setItem(TOAST_KEY, msg); } catch (e) {} }
      location.reload();
      return;
    }
    if (window.__bfcmReveal) window.__bfcmReveal(document, true);
    setTimeout(measureVeil, 50);
    if (msg) toast(msg, 5200);
  }

  // ---------------------------------------------------------------- wishlist "Move to bag"
  var probeCache = {};
  function productPath(btn) {
    var tile = btn.closest('[data-pid], .quick-cart__item, .account__wishlist-card');
    var a = tile && tile.querySelector('a[href*="/products/"]');
    var href = a ? a.getAttribute('href') : '';
    if (!href && tile && window.RastahWishlist && RastahWishlist.getLocalItems) {
      var pid = String(tile.getAttribute('data-pid') || '');
      var it = RastahWishlist.getLocalItems().filter(function (i) { return String(i.empi) === pid; })[0];
      href = it ? it.du : '';
    }
    if (!href) return '';
    try { return new URL(href, location.origin).pathname; } catch (e) { return ''; }
  }
  function probe(path) {
    if (probeCache[path]) return probeCache[path];
    var theme = window.Shopify && Shopify.theme;
    var url = path + '?section_id=bfcm-probe' + (theme && theme.role !== 'main' && theme.id ? '&preview_theme_id=' + theme.id : '');
    probeCache[path] = fetch(url, { credentials: 'same-origin' }).then(function (r) { return r.text(); }).then(function (html) {
      var m = html.match(/data-bfcm-drop="([a-z_]+)"/);
      return { lock: /data-lock="1"/.test(html), drop: m ? m[1] : '' };
    }).catch(function () { delete probeCache[path]; return { lock: false, drop: '' }; });
    return probeCache[path];
  }
  function replay(btn) {
    if (!btn || !btn.isConnected) return;
    btn.__bfcmOk = true;
    try { btn.click(); } finally { btn.__bfcmOk = false; }
  }
  function moveNeedsCheck() { return (GATE && !unlocked()) || PREVIEW || (EA && eaClosed()); }
  function checkMove(btn) {
    var path = productPath(btn);
    if (!path) return replay(btn);
    probe(path).then(function (info) {
      if (PREVIEW && info.drop === 'preview') {
        toast(launchAt() ? 'Sizes open at launch: ' + launchAt() + '.' : 'Sizes open at launch.');
        return;
      }
      if (EA && eaClosed() && info.drop) {
        toast((cfg.eaLabel || 'Opens 1:00 PM EST') + '. Early access is for the list.');
        return;
      }
      if (GATE && !unlocked() && info.lock) {
        pending = function () { replay(btn); };
        openSheet('wishlist_move', 'sale');
        return;
      }
      replay(btn);
    });
  }

  // ---------------------------------------------------------------- events
  // Capture phase on window runs before every theme / app handler, so a
  // locked Add to cart never reaches Stiletto's ProductForm or /cart/add.
  window.addEventListener('click', function (e) {
    var t = e.target;
    if (!(t instanceof Element)) return;

    // Typo suggestion: "Use it" fills the fix and unlocks in the same tap.
    var use = t.closest('[data-bfcm-sug-use]');
    if (use) {
      e.preventDefault();
      var uf = use.closest('form'), ub = uf && uf.querySelector('[data-bfcm-sug]');
      if (uf && ub && ub.getAttribute('data-val')) {
        uf.querySelector('input[type="email"]').value = ub.getAttribute('data-val');
        track('bfcm_typo_accept', { gate_origin: formOrigin(uf) });
        onSubmit(uf, true);
      }
      return;
    }

    // Returning visitor without a number: step 2 in place (outside a surface: in the sheet).
    var ra = t.closest('[data-bfcm-reask]');
    if (ra) {
      e.preventDefault();
      var rs = ra.closest('[data-bfcm-surface]');
      if (rs) {
        rs.__bfcmOrigin = 'reask';
        var rp = setState(rs, 'phone', true);
        focusIn(rp);
        track('bfcm_gate_open', { gate_origin: 'reask', gate_flow: 'sale', gate_step: 'phone', gate_surface: rs.getAttribute('data-bfcm-surface') });
      } else {
        openStep2('reask');
      }
      return;
    }

    // "Not now" on a surface's step 2.
    var nn = t.closest('[data-bfcm-surface] [data-bfcm-notnow]');
    if (nn) {
      e.preventDefault();
      var ns = nn.closest('[data-bfcm-surface]');
      skipped(ns.getAttribute('data-bfcm-surface'), 'not_now');
      var np = setState(ns, 'done', true);
      if (np) { try { np.focus({ preventScroll: true }); } catch (x) {} }
      return;
    }

    // Phone step: WhatsApp <-> SMS (only offered where SMS works).
    if (t.closest('[data-bfcm-chan-switch]')) {
      e.preventDefault();
      chanTouched = true;
      setChannel(channel === 'sms' ? 'whatsapp' : 'sms');
      var tf = t.closest('.bfcm-links');
      var tin = tf && tf.previousElementSibling && tf.previousElementSibling.querySelector('input[type="tel"]');
      if (tin) { try { tin.focus({ preventScroll: true }); } catch (x) {} }
      return;
    }

    if (sheet && !sheet.hidden) {
      if (t.closest('[data-bfcm-close]') || t === sheet) { e.preventDefault(); closeSheet(true); return; }
      var dn = t.closest('[data-bfcm-done]');
      if (dn) {
        // Shop pre-sale: to the pre-sale page when set and not already there; otherwise just close.
        var href = dn.getAttribute('href') || '';
        var go = href && href !== '#' && href.split(/[?#]/)[0] !== location.pathname;
        if (!go) e.preventDefault();
        closeSheet(false);
        return;
      }
      if (t.closest('[data-bfcm-notnow]')) {
        e.preventDefault();
        if (flow === 'sale') {
          // Pre-sale step 2 skipped ("No thanks, start shopping"): the drawer closes onto the unlocked
          // page, and a waiting Add to bag goes through.
          skipped('sheet', 'not_now');
          closeSheet(false);
          return;
        }
        closeSheet(true);
        toast(unlocked() ? 'BFCM pricing stays unlocked. The preview stays blurred until you add a number.' : 'No problem. The preview stays blurred.');
        return;
      }
      return;
    }

    // Contract (1): [data-bfcm-open="<origin>"] (menu, header badge, announcement, banner, …).
    var opener = t.closest('[data-bfcm-open]');
    if (opener) {
      if (openFrom(opener)) {
        e.preventDefault();
        e.stopImmediatePropagation();
      }
      return;
    }

    // Save for launch (a Swym wishlist button; assets/wishlist.js toggles it in bubble phase).
    var save = t.closest('[data-bfcm-save]');
    if (save) {
      var wasSaved = save.getAttribute('aria-pressed') === 'true';
      setTimeout(function () {
        var nowSaved = save.getAttribute('aria-pressed') === 'true';
        if (nowSaved === wasSaved) return; // wishlist.js ignored the tap (still loading)
        if (!nowSaved) { announce('Removed from your saved pieces.'); return; }
        track('bfcm_save_for_launch', {
          product_id: save.getAttribute('data-wishlist-product-id'),
          product_handle: save.getAttribute('data-wishlist-product-handle'),
          surface: save.closest('.rs') ? 'stories' : (save.closest('.product-item') ? 'card' : 'pdp'),
          hub_tab: hubTab()
        });
        if (firstSave()) toast('Saved for launch. It’ll be at the top of your early-access link ' + (isSms() ? 'by text' : 'on WhatsApp') + '.', 5200);
        else announce('Saved for launch.');
      }, 0);
      return;
    }

    // Wishlist Move to bag: price lock / preview / early-access checks by product.
    var mv = t.closest(MOVE);
    if (mv && !mv.__bfcmOk && moveNeedsCheck()) {
      e.preventDefault();
      e.stopImmediatePropagation();
      checkMove(mv);
      return;
    }

    // Preview week: a blurred piece or "Unlock the preview" opens the two-step unlock.
    var pvBtn = t.closest('[data-bfcm-preview-open]');
    if (pvBtn || previewScope(t)) {
      if (!pvBtn && !sheet) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      openPreview(pvBtn ? pvBtn.getAttribute('data-bfcm-origin') || 'button' : (t.closest('.product') ? 'pdp_gallery' : 'card'));
      return;
    }

    // Early access: drop Add to bag on a device that isn't in.
    var eaAtc = EA && t.closest(ATC);
    if (eaAtc && eaScope(eaAtc)) {
      e.preventDefault();
      e.stopImmediatePropagation();
      toast((cfg.eaLabel || 'Opens 1:00 PM EST') + '. Early access is for the list.');
      return;
    }

    if (!GATE || unlocked()) return;

    var atc = t.closest(ATC);
    if (atc && lockedScope(atc)) {
      if (atc.disabled || atc.getAttribute('aria-disabled') === 'true') return; // sold out etc.
      // The sticky bar / size & fit CTA proxy their click to the primary
      // button; let them, and catch the proxied click instead (so the replay
      // after unlock goes through the same path). Sticky bar with no size
      // picked just opens its tray.
      if (atc.matches('[data-mobile-sticky-atc-btn], [data-size-fit-cta]')) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      pending = atc.matches('.shopify-payment-button') ? null : atc;
      // A tap on the product page's own Add to bag: its waitlist box, in place. Proxied clicks
      // (sticky bar, size & fit sheet) and everything else: the sheet.
      var abox = hubPanelFor(atc) || (e.isTrusted ? pdpBoxFor(atc) : null);
      if (abox) { openSurface(abox, 'add_to_cart', !!pending); return; }
      openSheet(where(atc, 'add_to_cart'));
      return;
    }

    var price = t.closest(PRICE);
    if (price && lockedScope(price)) {
      e.preventDefault();
      e.stopImmediatePropagation();
      pending = null;
      var pbox = hubPanelFor(price) || pdpBoxFor(price);
      if (pbox) { openSurface(pbox, 'price_chip', false); return; }
      openSheet(where(price, 'price_chip'));
      return;
    }

    // Hub price_lock grid without the waitlist panel: first card tap per session opens the
    // email step. With the panel (the pre-sale page), cards just open their product page.
    if (hubLocked() && !dismissed() && !hubPanel()) {
      var card = t.closest(HUB_GRID + ' .product-item');
      // Wishlist hearts keep working (only look inside the card: Swym puts
      // swym-* classes on <body>).
      var heart = card && t.closest('[data-wishlist-btn], [class*="swym"]');
      if (card && !(heart && card.contains(heart))) {
        e.preventDefault();
        e.stopImmediatePropagation();
        pending = null;
        openSheet('card');
      }
    }
  }, true);

  window.addEventListener('submit', function (e) {
    var form = e.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (form.hasAttribute('data-bfcm-form')) {
      e.preventDefault();
      e.stopImmediatePropagation();
      onSubmit(form, form.__bfcmTypoFor && form.__bfcmTypoFor === (form.querySelector('input[type="email"]').value || '').trim());
      return;
    }
    if (form.hasAttribute('data-bfcm-phone-form')) {
      e.preventDefault();
      e.stopImmediatePropagation();
      onPhoneSubmit(form);
      return;
    }
    if (!/\/cart\/add/.test(form.getAttribute('action') || '')) return;
    // Enter-key / programmatic submits.
    if (eaScope(form)) {
      e.preventDefault();
      e.stopImmediatePropagation();
      toast((cfg.eaLabel || 'Opens 1:00 PM EST') + '. Early access is for the list.');
      return;
    }
    if (GATE && !unlocked() && lockedScope(form)) {
      e.preventDefault();
      e.stopImmediatePropagation();
      pending = form.querySelector('[data-add-to-cart], button[name="add"]');
      openSheet(where(form, 'add_to_cart'));
    }
  }, true);

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && sheet && !sheet.hidden) closeSheet(true);
  });

  // Hub tab swaps: collection-tabs.js flips the active tab, swaps the grid and
  // fires shopify:section:load synchronously. The nav's own sync runs a tick
  // later; mirror the mode now so the new grid never paints in the old mode.
  document.addEventListener('shopify:section:load', function () {
    var hub = document.querySelector('[data-bfcm-hub]');
    var t = hub && hub.querySelector('.collection-view-switcher__tab--active');
    if (t) hub.setAttribute('data-bfcm-mode', t.getAttribute('data-bfcm-mode') || 'standard');
    if (PREVIEW && window.__bfcmReveal) window.__bfcmReveal(document, false);
  });

  // Height of the hub's visible sticky box, so the floating mobile filter button can sit above it.
  function measureVeil() {
    var vs = document.querySelectorAll('[data-bfcm-veil]');
    for (var i = 0; i < vs.length; i++) {
      if (vs[i].offsetHeight) { root.style.setProperty('--bfcm-veil-h', (vs[i].offsetHeight + 16) + 'px'); return; }
    }
  }
  measureVeil();
  window.addEventListener('resize', measureVeil);
  document.addEventListener('shopify:section:load', function () { setTimeout(measureVeil, 50); });
  document.addEventListener('bfcm:tab', function () { setTimeout(measureVeil, 50); });

  // Another tab unlocked this device.
  window.addEventListener('storage', function (e) {
    if (e.key === KEY && e.newValue) {
      root.classList.add('bfcm-unlocked');
      surfaces().forEach(function (sf) { if (sf.getAttribute('data-bfcm-state') === 'email') setState(sf, 'joined'); });
    }
    if (e.key === PKEY && e.newValue) {
      // A number was given in another tab: the re-ask goes away here too (html.bfcm-phone).
      root.classList.add('bfcm-phone');
      if (PREVIEW && !previewOpen()) {
        root.classList.add('bfcm-preview-open');
        if (window.__bfcmReveal) window.__bfcmReveal(document, false);
      }
    }
  });

  // Back to a page from the bfcache (e.g. after the home form's redirect): no stuck busy form.
  window.addEventListener('pageshow', function (e) {
    if (!e.persisted) return;
    surfaces().forEach(function (sf) {
      if (!sf.hasAttribute('data-bfcm-busy')) return;
      sf.removeAttribute('data-bfcm-busy');
      if (unlocked()) setState(sf, 'joined');
    });
  });

  // Theme editor re-renders a section: its surfaces start in the right state again.
  document.addEventListener('shopify:section:load', function () { setTimeout(initSurfaces, 0); });

  initPhone();
  if (PREVIEW) {
    // Cards rendered after load (infinite scroll, recommendations, search) get
    // their real photos too once the preview is unlocked.
    if (previewOpen() && window.__bfcmReveal) window.__bfcmReveal(document, false);
    if ('MutationObserver' in window) {
      var queued = false;
      new MutationObserver(function (records) {
        if (!previewOpen() || queued) return;
        for (var i = 0; i < records.length; i++) {
          var nodes = records[i].addedNodes;
          for (var j = 0; j < nodes.length; j++) {
            var n = nodes[j];
            if (n.nodeType === 1 && (n.matches('template[data-bfcm-real]') || n.querySelector('template[data-bfcm-real]'))) {
              queued = true;
              requestAnimationFrame(function () { queued = false; window.__bfcmReveal(document, false); });
              return;
            }
          }
        }
      }).observe(document.body, { childList: true, subtree: true });
    }
    if (hubMode() === 'preview' && !document.querySelector(HUB_GRID + ' [data-bfcm-drop]')) {
      console.warn('[bfcm-gate] The Preview tab\'s collection is not in Theme settings → BFCM 2026 → Preview collections, so its photos are not held back.');
    }
  }

  // Early access ends at the public launch: stop greying out Add to bag on the minute.
  if (EA && eaClosed() && cfg.launchAt) {
    var left = cfg.launchAt - Date.now();
    if (left <= 0) root.classList.remove('bfcm-ea-closed');
    else if (left < 864e5) setTimeout(function () { root.classList.remove('bfcm-ea-closed'); }, left);
  }

  arrive();
  initSurfaces();
  tagCart();

  viewedDrop();
  autoKnown();

  // Klaviyo requests an earlier page view could not finish.
  setTimeout(flushQueue, 2500);
  window.addEventListener('online', flushQueue);

  // Toast carried across the reload after a product-page unlock.
  try {
    var carried = sessionStorage.getItem(TOAST_KEY);
    if (carried) { sessionStorage.removeItem(TOAST_KEY); setTimeout(function () { toast(carried, 5200); }, 300); }
  } catch (e) {}

  window.RastahBFCMGate = {
    open: function (origin) { openSheet(origin, 'sale'); },
    openPreview: openPreview,
    // The sheet on step 2 (joined devices); false when it can't (no sheet, not joined, …).
    step2: function (origin) { return unlocked() ? openStep2(origin) : false; },
    unlocked: unlocked,
    phoneGiven: phoneGiven,
    previewUnlocked: previewOpen,
    eaClosed: eaClosed,
    // Show the success screen ('sale' | 'preview') for custom sign-up flows and QA.
    // Returns false when it isn't available (stage, setting off, early access started).
    success: function (kind) { return showDone(kind === 'preview' ? 'preview' : 'sale'); },
    /*
     * For custom Add to bag flows (e.g. Shop the Look):
     *   RastahBFCMGate.require(function () { addToBag(); }, 'shop_the_look');
     * Runs cb now when unlocked (or outside the price-lock stages); otherwise
     * opens the email step and runs cb after unlock. Dismissing drops cb.
     * Returns true if cb ran now. Pair with canBuy(el) for drop products.
     */
    require: function (cb, origin) {
      if (!GATE || unlocked() || !sheet) { cb(); return true; }
      pending = cb;
      openSheet(origin || 'require', 'sale');
      return false;
    },
    // Is this element price-locked right now?
    isLocked: lockedScope,
    // Can this element's product be added to the bag on this device right now?
    // false for a blurred preview piece and for a drop product during the
    // early-access hour on a device that isn't in.
    canBuy: function (el) {
      if (!el) return true;
      if (PREVIEW && el.closest('[data-bfcm-drop="preview"]')) return false;
      return !eaScope(el);
    },
    reset: function () {
      try { localStorage.removeItem(KEY); localStorage.removeItem(PKEY); localStorage.removeItem(KNOWN_KEY); localStorage.removeItem(SAVE_TOAST_KEY); sessionStorage.removeItem(DISMISS_KEY); sessionStorage.removeItem(EM_KEY); } catch (e) {}
      lastEmail = '';
      saveToastSeen = false;
      document.cookie = KEY + '=; path=/; max-age=0';
      document.cookie = PKEY + '=; path=/; max-age=0';
      root.classList.remove('bfcm-unlocked', 'bfcm-preview-open', 'bfcm-phone', 'bfcm-known');
      root.removeAttribute('data-bfcm-done-v');
      surfaces().forEach(function (sf) { setState(sf, 'email'); });
    }
  };
})();
